import { Add, CreateOutlined } from "@mui/icons-material";
import { doc, updateDoc } from "firebase/firestore";
import React, { useContext, useEffect, useState, useRef } from "react";
import { Sheet } from "react-modal-sheet";
import { useLocation, useNavigate } from "react-router-dom";
import { collectionPath } from "../../context/EnvContext";
import SessionReplayContext from "../../context/SessionReplayContext";
import { UserContext } from "../../context/UserContext";
import AddressBox from "../../elements/AddressBox.jsx";
import AddressForm from "../../elements/AddressForm";
import FAQ from "../../elements/FAQ";
import Footer from "../../elements/Footer";
import FPButton from "../../elements/FPButton.jsx";
import Header from "../../elements/Header";
import ShippingCheckoutBanner from "../../elements/ShippingCheckoutBanner";
import ShippingOptions from "../../elements/ShippingOptions";
import StickyFooter from "../../elements/StickyFooter.jsx";
import Summary from "../../elements/Summary";
import { useFirestore } from "../../firebase/FirestoreContext";
import noop from "../../functions/noop";
import useCart from "../../hooks/useCart";
import useCartValidations from "../../hooks/useCartValidations";
import analyticsHelper from "../../utils/analyticsHelper";
import CheckoutAuthBanner from "../../elements/CheckoutAuthBanner.jsx";
import AuthorizeNetPaymentForm from "../../elements/AuthorizeNetPaymentForm.jsx";
import SavedPaymentMethod from "../../elements/SavedPaymentMethod.jsx";
import PaymentProcessingOverlay from "../../elements/PaymentProcessingOverlay.jsx";
import {
  payWithAuthorizeNet,
  listSavedCards,
  deleteSavedCard,
  updateSavedCardBilling,
  SAVED_CARDS_ENABLED,
} from "../../api/authorizeNet";

/**
 * Card checkout backed by Authorize.Net.
 *
 * Adapted from ManualCheckout (Zelle/Venmo), which it replaces once card
 * payments are signed off. The shipping-address, shipping-speed and summary
 * sections are carried over unchanged; only the payment section and the submit
 * path differ.
 *
 * PAYMENT FLOW:
 *   new card    -> Accept UI modal yields a single-use nonce -> POST to server
 *   saved card  -> POST storedCardId; nothing card-shaped touches this bundle
 *
 * The card fields live in an Authorize.Net-hosted iframe, so the PAN never
 * enters our DOM (PCI SAQ A).
 */
/**
 * Checkbox + label rows.
 *
 * `alignItems: center` alone does not align these: browsers ship a default
 * margin on <input type="checkbox"> (Chrome uses 3px 3px 3px 4px), which
 * pushes the box off-centre against its label. Zeroing that margin and giving
 * the box explicit dimensions is what actually makes them line up.
 */
const checkboxRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  fontSize: 14,
  lineHeight: 1.4,
  color: "#444",
  cursor: "pointer",
};

const sectionStyle = { marginTop: 24 };
const sectionHeadingStyle = { fontSize: 18, margin: "0 0 12px 0" };

const checkboxInputStyle = {
  width: 16,
  height: 16,
  margin: 0,
  flexShrink: 0,
  accentColor: "#3b72fe",
  cursor: "pointer",
};

const CheckoutAuthorizeNet = () => {
  const { validateCart } = useCartValidations();
  const {
    summary,
    appliedDiscount,
    cartItems,
    wallet,
    cart,
    refresh,
    refreshedAtTime,
    selectedShippingData,
    setSelectedShippingData,
  } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const db = useFirestore();
  const firestore = useFirestore();
  const { user, loading } = React.useContext(UserContext);

  const [addresses, setAddresses] = useState([]);
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [hasFetchedData, setHasFetchedData] = useState(false);

  const [shippingAddress, setShippingAddress] = useState();
  const [shippingAddressValid, setShippingAddressValid] = useState(false);

  const [showStickyFooter, setShowStickyFooter] = useState(true);

  // --- payment state ---------------------------------------------------------
  // null = pay with a new card via Accept UI; otherwise a customerPaymentProfileId.
  const [selectedSavedCardId, setSelectedSavedCardId] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  // Opt-OUT: pre-checked, customer unticks to decline. Common US practice, and
  // the label states plainly what happens. Note the card networks' stored-
  // credential rules still require the intent to be disclosed at the point of
  // capture -- which the visible label does.
  const [savePaymentMethod, setSavePaymentMethod] = useState(true);

  // Guests check out on an anonymous Firebase session, which lives only in this
  // browser's storage. A card saved to it is unreachable once they clear data or
  // switch device, and until then anyone using this browser profile could charge
  // it -- there is no password and no way to revoke. So we don't offer it.
  // The server enforces the same rule from the verified token.
  // Also gated on SAVED_CARDS_ENABLED: a stored profile cannot carry a card
  // code, so charging one fails while the account requires it. See the note in
  // api/authorizeNet.js.
  const canSaveCard =
    SAVED_CARDS_ENABLED && Boolean(user) && !user.isAnonymous;
  const [paymentError, setPaymentError] = useState(null);
  // Set when the gateway held the charge for fraud review -- neither a receipt
  // nor a decline, so it gets its own terminal screen state.
  const [heldForReview, setHeldForReview] = useState(false);
  // Distinct from isLoading: this drives the blocking overlay, and it must stay
  // up while we navigate to the confirmation page. Clearing it in a plain
  // finally would flash the untouched checkout between the charge succeeding
  // and the next route mounting -- exactly the "did it work?" moment the
  // overlay exists to remove.
  const [processing, setProcessing] = useState(false);

  // --- billing address -------------------------------------------------------
  // Defaults to FALSE so the billing form is expanded on arrival; ticking the
  // box collapses it and reuses the shipping address.
  //
  // Why this matters beyond tidiness: billTo.address and billTo.zip are what the
  // card issuer checks for AVS. Assuming billing == shipping silently mismatches
  // whenever someone ships to an address other than their card's (gifts,
  // work addresses), which can decline an otherwise good card.
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(false);
  const [billingAddress, setBillingAddress] = useState(null);
  const [billingAddressValid, setBillingAddressValid] = useState(false);

  // Editing the billing address held in the vault for a SAVED card. Separate
  // state from the new-card billing form because it writes to Authorize.Net
  // rather than riding along with the charge.
  const [editingSavedBilling, setEditingSavedBilling] = useState(false);
  const [savedBillingDraft, setSavedBillingDraft] = useState(null);
  const [savedBillingValid, setSavedBillingValid] = useState(false);
  const [savingBilling, setSavingBilling] = useState(false);

  const payButtonRef = useRef(null);

  // Guards against a double-submit racing two charges for the same cart. The
  // server's duplicateWindow would reject the second, but this keeps the UI
  // honest and avoids a confusing duplicate-transaction error.
  const submittingRef = useRef(false);

  const selectedAddress = shippingAddressValid
    ? shippingAddress
    : addresses[selectedAddressIndex];

  const { enableSessionReplay } = useContext(SessionReplayContext);

  const { id: cartID } = cart || {};
  const { cart_sooperstock_ID = null } = location.state || {};

  const [isOpen, setOpen] = useState(false);
  const [showAddNewForm, setShowAddNewForm] = useState(false);
  const [tempSelectedAddressIndex, setTempSelectedAddressIndex] = useState(0);

  useEffect(() => {
    enableSessionReplay();
  }, [enableSessionReplay]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleAddressSelect = (index) => {
    setTempSelectedAddressIndex(index);
  };

  const handleSaveAddressChange = () => {
    setSelectedAddressIndex(tempSelectedAddressIndex);

    if (tempSelectedAddressIndex !== 0) {
      const updatedAddresses = [...addresses];
      const selected = updatedAddresses[tempSelectedAddressIndex];
      updatedAddresses.splice(tempSelectedAddressIndex, 1);
      updatedAddresses.unshift(selected);
      setAddresses(updatedAddresses);
      setSelectedAddressIndex(0);
    }

    setOpen(false);
  };

  const handleAddNewAddress = () => {
    setOpen(false);
    setShowAddNewForm(true);
  };

  const toggleAddressOptions = () => {
    setTempSelectedAddressIndex(selectedAddressIndex);
    setOpen(true);
  };

  useEffect(() => {
    function checkPayVisibility() {
      if (!payButtonRef.current) return;
      const rect = payButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }
    window.addEventListener("scroll", checkPayVisibility, { passive: true });
    window.addEventListener("resize", checkPayVisibility);
    const timeoutId = setTimeout(checkPayVisibility, 50);
    return () => {
      window.removeEventListener("scroll", checkPayVisibility);
      window.removeEventListener("resize", checkPayVisibility);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!payButtonRef.current) return;
      const rect = payButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [summary, cartItems.length, isLoading]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        setIsLoading(false);
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  const saveAddress = async (address) => {
    const updatedAddresses = [address, ...addresses];
    setAddresses(updatedAddresses);
    setSelectedAddressIndex(0);

    analyticsHelper.identify(
      { ...user, email: user.email || address.email },
      () => analyticsHelper.addedAddress(address),
    );

    await updateDoc(doc(firestore, "Users/UsersList/DataBase", user.uid), {
      addresses: updatedAddresses,
    });

    if (user?.email) {
      setShowAddNewForm(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        await fetchCheckoutData();
        setHasFetchedData(true);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (!loading && !hasFetchedData && (user?.uid || !user) && cartID) {
      loadData();
    }
  }, [user?.uid, cartID, firestore, collectionPath, loading, hasFetchedData]);

  useEffect(() => {
    if (isMounted) return noop;
    if (cartItems.length > 0 && summary.total > 0.01 && wallet) {
      analyticsHelper.startedCheckout({
        cartItems,
        summary,
        cart_sooperstock_ID,
        wallet,
      });
      setIsMounted(true);
    }
  }, [isMounted, cartItems.length, cart_sooperstock_ID, summary.total, wallet]);

  const fetchCheckoutData = async () => {
    try {
      if (!user || !cartID) return;
      const cartDocRef = doc(
        firestore,
        `${collectionPath}Carts/CartsList/DataBase`,
        cartID,
      );
      await updateDoc(cartDocRef, { opening_checkout_page: new Date() });
    } catch (error) {
      console.error("Error fetching checkout data:", error);
    }
  };

  useEffect(() => {
    const fetchAddresses = () => {
      if (!loading && !user?.email) {
        setShowAddNewForm(true);
      } else if (!loading && user?.addresses?.length) {
        setAddresses(user.addresses);
        setShowAddNewForm(false);
      } else if (!loading && (!user?.addresses || user.addresses.length === 0)) {
        setShowAddNewForm(true);
      }
    };
    if (!loading) fetchAddresses();
  }, [loading, user?.email, user?.addresses]);

  const saveShippingOption = async (shippingData) => {
    const docRef = doc(db, `${collectionPath}Carts/CartsList/DataBase`, cartID);
    await updateDoc(docRef, {
      shippingOption: shippingData.shippingOption || null,
      shippingCost: shippingData?.shippingCost || 0,
      shipping_cost: shippingData?.shippingCost || 0,
      free_shipping_threshold: shippingData?.shippingOption?.free_threshold || 0,
    });
  };

  useEffect(() => {
    if (!refreshedAtTime) return () => {};
    if (!cartID) return () => {};
    const checkCart = async () => {
      const cartErrors = await validateCart(cartID);
      if (cartErrors.length > 0) navigate("/cart");
    };
    checkCart();
  }, [refreshedAtTime]);

  // --- saved cards -----------------------------------------------------------
  useEffect(() => {
    // Wait for auth to settle before asking for saved cards -- same gating
    // CheckoutSquare uses. Firing while UserContext is still loading sends a
    // request without a usable token; it 401s, listSavedCards yields an empty
    // list, and nothing retries. The symptom was a saved card (and its billing
    // address) that only appeared after a manual page refresh, because on a
    // refresh the token is present from the first render.
    //
    // Depending on uid as well as the token means signing in mid-session
    // re-fetches rather than keeping the anonymous user's empty list.
    // Don't even ask when saved cards are off -- nothing can be rendered from
    // the result, and a stored card is not chargeable in this configuration.
    if (!SAVED_CARDS_ENABLED) return undefined;
    if (loading || !user || user.isAnonymous || !user.accessToken) return undefined;

    let cancelled = false;
    listSavedCards(user)
      .then((cards) => {
        if (cancelled) return;
        setSavedCards(cards);
        // Default to the first saved card when one exists -- fewer taps for a
        // returning customer.
        if (cards.length) setSelectedSavedCardId(cards[0].id);
      })
      .catch((error) => console.error("Failed to load saved cards:", error));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user?.uid, user?.accessToken, user?.isAnonymous]);

  const selectedSavedCard =
    savedCards.find((c) => c.id === selectedSavedCardId) || null;

  /** One-line rendering of a gateway billTo block. */
  const formatBillTo = (b) => {
    if (!b) return null;
    const name = [b.firstName, b.lastName].filter(Boolean).join(" ");
    const line = [b.address, b.city, [b.state, b.zip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", ");
    return [name, line].filter(Boolean).join(" — ") || null;
  };

  /**
   * Persist a new billing address onto the saved card at the gateway.
   *
   * This writes to Authorize.Net rather than being sent with the charge,
   * because a stored card's billTo comes from the vault and cannot be
   * overridden per-transaction (E00093).
   */
  const handleSaveSavedCardBilling = async () => {
    if (!savedBillingValid || !savedBillingDraft || !selectedSavedCardId) return;
    setSavingBilling(true);
    setPaymentError(null);

    const billTo = {
      firstName: savedBillingDraft.first_name || "",
      lastName: savedBillingDraft.last_name || "",
      address: savedBillingDraft.street_address || "",
      city: savedBillingDraft.city || "",
      state: savedBillingDraft.state || "",
      zip: savedBillingDraft.postalCode || "",
      country: savedBillingDraft.country || "USA",
    };

    try {
      const { ok, error } = await updateSavedCardBilling(
        selectedSavedCardId,
        billTo,
        user,
      );
      if (!ok) {
        setPaymentError(error || "Couldn't update the billing address.");
        return;
      }
      // Re-read from the gateway rather than patching local state, so what we
      // show is what will actually be charged against. Guard against a failed
      // re-read blanking a list we know is non-empty -- the write succeeded.
      const refreshed = await listSavedCards(user);
      if (refreshed.length) setSavedCards(refreshed);
      setEditingSavedBilling(false);
      setSavedBillingDraft(null);
      setSavedBillingValid(false);
    } catch (error) {
      console.error("Failed to update saved card billing:", error);
      setPaymentError("Couldn't update the billing address. Please try again.");
    } finally {
      setSavingBilling(false);
    }
  };

  const handleDeleteSavedCard = async (paymentProfileId) => {
    const ok = await deleteSavedCard(paymentProfileId, user);
    if (!ok) {
      setPaymentError("Couldn't remove that card. Please try again.");
      return;
    }
    setSavedCards((prev) => prev.filter((c) => c.id !== paymentProfileId));
    setSelectedSavedCardId((current) =>
      current === paymentProfileId ? null : current,
    );
  };

  /**
   * Persist address + shipping, then charge.
   *
   * `paymentSource` is either { opaqueData } (new card, from the Accept UI
   * modal) or { storedCardId } (saved card).
   */
  const submitPayment = async (paymentSource) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setIsLoading(true);
    setProcessing(true);
    setPaymentError(null);

    // Set only on the path that leaves this page for the receipt, so the
    // overlay survives the transition instead of blinking off first.
    let navigatingToReceipt = false;

    try {
      const cartErrors = await validateCart(cartID);
      if (cartErrors.length > 0) {
        navigate("/cart");
        return;
      }

      await saveShippingOption(selectedShippingData);

      if (shippingAddress && shippingAddressValid) {
        await saveAddress(shippingAddress);
      }

      const finalAddress = shippingAddressValid
        ? shippingAddress
        : addresses[selectedAddressIndex];

      const currentEmail = user?.email || finalAddress?.email;
      if (!currentEmail) {
        setPaymentError("Please provide your email address to finish your order.");
        return;
      }
      if (finalAddress && !finalAddress.email) {
        finalAddress.email = currentEmail;
      }

      await updateDoc(
        doc(firestore, `${collectionPath}Carts/CartsList/DataBase`, cartID),
        {
          shipmentAddress: finalAddress,
          isAnonymous: user?.isAnonymous || false,
          walletDeduction: summary.paymentTotals.wallet || 0,
          paid_amount_total: Math.round(summary.total * 100),
        },
      );

      const { status, data } = await payWithAuthorizeNet(
        {
          cartID,
          // Card portion only -- the wallet balance is applied separately.
          amount: Math.round(summary.paymentTotals.cc * 100),
          walletDeduction: Math.round((summary.paymentTotals.wallet || 0) * 100),
          shippingAddress: finalAddress,
          // When "same as shipping" is ticked we send the shipping address as
          // billTo; otherwise the separately-entered billing address. The
          // server maps whichever it receives onto billTo for AVS.
          billingAddress: billingSameAsShipping
            ? finalAddress
            : billingAddress || finalAddress,
          savePaymentMethod:
            Boolean(paymentSource.opaqueData) && savePaymentMethod && canSaveCard,
          ...paymentSource,
          metadata: {
            discount_code: appliedDiscount?.code || null,
            cart_id: cartID,
          },
        },
        user,
      );

      // 202: accepted but held by the fraud filters. Must NOT read as a
      // completed order -- no receipt, no order page.
      if (status === 202 && data.held) {
        setHeldForReview(true);
        return;
      }

      if (data.success) {
        navigatingToReceipt = true;
        navigate(`/payment-success?cart_id=${cartID}`, { replace: true });
        return;
      }

      // The charge went through but order creation failed. Retrying would
      // double-charge, so this is deliberately NOT presented as a retryable error.
      if (data.charged) {
        setPaymentError(
          data.error ||
            "Your payment went through but we hit a problem creating your order. " +
              "Our team has been notified — please don't retry.",
        );
        return;
      }

      setPaymentError(
        data.error || "We couldn't process your payment. Please try again.",
      );
    } catch (error) {
      console.error("Authorize.Net payment error:", error);
      setPaymentError("We couldn't process your payment. Please try again.");
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
      if (!navigatingToReceipt) setProcessing(false);
    }
  };

  /** Accept UI handed us a nonce; charge it. */
  const handleTokenReceived = (opaqueData) => {
    // AcceptUI's modal tears down asynchronously after Pay is clicked. Raising
    // the overlay here rather than waiting for submitPayment's first state
    // update removes the beat where the modal has vanished and the checkout
    // page looks untouched.
    setProcessing(true);
    return submitPayment({ opaqueData });
  };

  /** Saved-card path -- no tokenization step. */
  const handlePayWithSavedCard = () =>
    submitPayment({ storedCardId: selectedSavedCardId });

  const addressReady =
    Boolean(selectedAddress) &&
    Boolean(selectedShippingData) &&
    !(showAddNewForm && !shippingAddressValid);

  // Charging a saved card uses the billing address stored in the vault -- the
  // gateway rejects the request outright if billTo is also sent (E00093). So
  // the billing section is meaningless on that path: hidden, and not gating.
  const payingWithSavedCard = selectedSavedCardId !== null;

  // A separate billing address must be complete before we let a charge through.
  // Sending a half-filled billTo is worse than sending the shipping address --
  // it guarantees an AVS mismatch rather than merely risking one.
  const billingReady =
    payingWithSavedCard || billingSameAsShipping || billingAddressValid;

  const disabled = !addressReady || !billingReady || isLoading;

  const amountLabel =
    typeof summary?.paymentTotals?.cc === "number"
      ? `$${summary.paymentTotals.cc.toFixed(2)}`
      : undefined;

  /**
   * Pay button for the SAVED-CARD path only.
   *
   * On the new-card path the Accept UI button is itself the trigger (it opens
   * the hosted modal and calls back with a nonce), so rendering a second submit
   * button there would be a dead control.
   */
  const PayButton = ({ style }) =>
    selectedSavedCardId === null ? null : (
      <FPButton
        $dark
        onClick={handlePayWithSavedCard}
        disabled={disabled}
        fontSize={"1.2rem"}
        padding={"10px"}
        background={"#3b72fe"}
        outlined={false}
        textColor={"white"}
        style={{ maxWidth: 600, width: "100%", ...style }}
      >
        {isLoading ? "Processing…" : `Pay ${amountLabel || ""}`.trim()}
      </FPButton>
    );

  return (
    <div style={{ backgroundColor: "white" }}>
      <Header search={false} />
      <div
        css={{
          maxWidth: "1140px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          paddingTop: 16,
        }}
      >
        <ShippingCheckoutBanner totalAmount={summary.subtotal} />

        {!user?.email && <CheckoutAuthBanner onSignInComplete={refresh} />}

        <div style={styles.checkoutContainer}>
          <h2 style={styles.orderTitle}>Shipping Details</h2>
          {!showAddNewForm && user?.email && (
            <div style={styles.address}>
              {addresses?.length > 0 ? (
                <AddressBox addressObject={addresses[selectedAddressIndex]}>
                  <button
                    style={styles.linkButton}
                    onClick={toggleAddressOptions}
                  >
                    <CreateOutlined
                      style={{ fontSize: ".8rem", position: "relative", top: 2 }}
                    />{" "}
                    Change Address
                  </button>
                </AddressBox>
              ) : (
                <button style={styles.linkButton} onClick={toggleAddressOptions}>
                  <CreateOutlined
                    style={{ fontSize: ".8rem", position: "relative", top: 2 }}
                  />{" "}
                  Please add an address
                </button>
              )}
            </div>
          )}
          {(showAddNewForm || !user?.email) && (
            <div>
              <AddressForm
                existingEmail={user?.email}
                onSave={saveAddress}
                onCancel={() => setShowAddNewForm(false)}
                showSaveButton={false}
                showCancelButton={addresses.length > 0 && user?.email}
                onChange={({ address, valid }) => {
                  setShippingAddress(address);
                  setShippingAddressValid(valid);
                }}
              />
            </div>
          )}

          <div>
            <h3 style={{ fontSize: 18 }}>Shipping Speed</h3>
            <p
              style={{
                fontSize: 13,
                color: "#666",
                lineHeight: 1.3,
                margin: "8px 0 16px 0",
              }}
            >
              Exact delivery timelines vary by address. Orders ship next
              business day except on holidays (*Note: Fri-Sun orders ship on
              Mon).
            </p>
            <ShippingOptions
              cartPrice={summary.subtotal}
              selectedSlug={selectedShippingData?.shippingOption?.slug}
              onChange={setSelectedShippingData}
            />
          </div>

          {/*
            Billing address. Expanded by default; ticking "same as shipping"
            collapses it. Sits before Payment because billTo is what the issuer
            verifies against the card (AVS) -- the customer should settle it
            before entering card details.
          */}
          {/*
            SAVED-CARD billing. The gateway charges a stored card against the
            billTo held in its vault and rejects any per-transaction override
            (E00093), so the shipping-address toggle below is meaningless here.
            Instead we show what's actually stored and let it be edited, which
            writes to Authorize.Net. Without this a customer whose billing
            address changed would get AVS declines with no way to fix them.
          */}
          {payingWithSavedCard && (
            <div style={sectionStyle}>
              <h3 style={sectionHeadingStyle}>Billing Details</h3>

              {!editingSavedBilling ? (
                <div
                  style={{
                    border: "1px solid #eee",
                    borderRadius: 8,
                    padding: "12px 16px",
                    fontSize: 13,
                    color: "#666",
                    lineHeight: 1.5,
                  }}
                >
                  {formatBillTo(selectedSavedCard?.billTo) ? (
                    <>
                      <div style={{ color: "#444" }}>
                        {formatBillTo(selectedSavedCard.billTo)}
                      </div>
                      <div style={{ marginTop: 4 }}>
                        Saved with this card — your bank verifies against it.
                      </div>
                    </>
                  ) : (
                    <div>No billing address saved with this card.</div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setEditingSavedBilling(true);
                      setSavedBillingValid(false);
                      setSavedBillingDraft(null);
                    }}
                    style={{
                      marginTop: 10,
                      fontSize: ".8rem",
                      color: "black",
                      cursor: "pointer",
                      border: "1px solid black",
                      borderRadius: 50,
                      padding: "5px 12px",
                      backgroundColor: "white",
                    }}
                  >
                    Update billing address
                  </button>
                </div>
              ) : (
                <>
                  <p
                    style={{
                      fontSize: 13,
                      color: "#666",
                      margin: "8px 0 12px 0",
                      lineHeight: 1.4,
                    }}
                  >
                    Enter the address associated with this card. Saving updates
                    it for future orders too.
                  </p>
                  <AddressForm
                    addressName="Billing Address"
                    existingEmail={user?.email || selectedAddress?.email || " "}
                    showSaveButton={false}
                    showCancelButton={false}
                    onChange={({ address, valid }) => {
                      setSavedBillingDraft(address);
                      setSavedBillingValid(valid);
                    }}
                  />
                  <div style={{ display: "flex", gap: 12, marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingSavedBilling(false);
                        setSavedBillingDraft(null);
                        setSavedBillingValid(false);
                      }}
                      style={{
                        flex: 1,
                        padding: 12,
                        border: "1px solid #ddd",
                        borderRadius: 50,
                        backgroundColor: "white",
                        cursor: "pointer",
                        fontSize: 15,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveSavedCardBilling}
                      disabled={!savedBillingValid || savingBilling}
                      style={{
                        flex: 2,
                        padding: 12,
                        border: "none",
                        borderRadius: 50,
                        backgroundColor: "#3b72fe",
                        color: "white",
                        cursor:
                          !savedBillingValid || savingBilling
                            ? "not-allowed"
                            : "pointer",
                        opacity: !savedBillingValid || savingBilling ? 0.5 : 1,
                        fontSize: 15,
                        fontWeight: 600,
                      }}
                    >
                      {savingBilling ? "Saving…" : "Save billing address"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <div
            style={{
              ...sectionStyle,
              display: payingWithSavedCard ? "none" : "block",
            }}
          >
            <h3 style={sectionHeadingStyle}>Billing Details</h3>

            <label style={{ ...checkboxRowStyle, margin: "10px 0 16px 0" }}>
              <input
                type="checkbox"
                style={checkboxInputStyle}
                checked={billingSameAsShipping}
                onChange={(e) => setBillingSameAsShipping(e.target.checked)}
              />
              <span>Billing address is the same as my shipping address</span>
            </label>

            {billingSameAsShipping ? (
              <div
                style={{
                  border: "1px solid #eee",
                  borderRadius: 8,
                  padding: "12px 16px",
                  fontSize: 13,
                  color: "#666",
                  lineHeight: 1.5,
                }}
              >
                Using your shipping address for billing.
                {selectedAddress?.address ? (
                  <div style={{ marginTop: 4, color: "#444" }}>
                    {selectedAddress.address}
                  </div>
                ) : null}
              </div>
            ) : (
              <>
                <p
                  style={{
                    fontSize: 13,
                    color: "#666",
                    margin: "0 0 12px 0",
                    lineHeight: 1.4,
                  }}
                >
                  Enter the address associated with your card. This helps your
                  bank verify the payment.
                </p>
                <AddressForm
                  addressName="Billing Address"
                  /* AddressForm shows and requires an email field unless
                     `existingEmail` is truthy. Billing doesn't need one -- we
                     already captured it with the shipping address -- so we pass
                     the known email, falling back to a non-empty placeholder
                     purely to suppress the field. The value is never used:
                     toGatewayAddress maps only name/street/city/state/zip onto
                     billTo. */
                  existingEmail={user?.email || selectedAddress?.email || " "}
                  showSaveButton={false}
                  showCancelButton={false}
                  onChange={({ address, valid }) => {
                    setBillingAddress(address);
                    setBillingAddressValid(valid);
                  }}
                />
              </>
            )}
          </div>

          <div style={sectionStyle}>
            <h3 style={sectionHeadingStyle}>Payment</h3>

            {!addressReady && (
              <p style={{ fontSize: 13, color: "#666", margin: "8px 0 16px 0" }}>
                Add a shipping address and choose a shipping speed to continue.
              </p>
            )}

            {addressReady && !billingReady && (
              <p style={{ fontSize: 13, color: "#666", margin: "8px 0 16px 0" }}>
                Complete your billing address above to continue.
              </p>
            )}

            {paymentError && (
              <div
                role="alert"
                style={{
                  background: "#fdecea",
                  border: "1px solid #f5c6cb",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 16,
                  fontSize: 14,
                  color: "#b00020",
                }}
              >
                {paymentError}
              </div>
            )}

            {SAVED_CARDS_ENABLED && savedCards.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {savedCards.map((pm) => (
                  <SavedPaymentMethod
                    key={pm.id}
                    pm={pm}
                    selectedPaymentMethod={selectedSavedCardId}
                    onSelect={() => setSelectedSavedCardId(pm.id)}
                    onDelete={() => handleDeleteSavedCard(pm.id)}
                  />
                ))}

                <button
                  type="button"
                  onClick={() => setSelectedSavedCardId(null)}
                  style={{
                    border:
                      selectedSavedCardId === null
                        ? "2px solid #3B72FE"
                        : "1px solid #ddd",
                    borderRadius: 8,
                    padding: 16,
                    width: "100%",
                    textAlign: "left",
                    background: "white",
                    cursor: "pointer",
                    fontSize: 15,
                    marginTop: 10,
                  }}
                >
                  <div>Use a different card</div>
                  {/* Selecting this swaps in the billing form and the Accept UI
                      button, which is not obvious from the label alone. */}
                  {selectedSavedCardId === null && (
                    <div style={{ marginTop: 4, fontSize: 13, color: "#666" }}>
                      Please enter billing address and card details
                    </div>
                  )}
                </button>
              </div>
            )}

            {selectedSavedCardId === null && (
              <>
                {canSaveCard && (
                  <label style={{ ...checkboxRowStyle, margin: "12px 0 16px 0" }}>
                    <input
                      type="checkbox"
                      style={checkboxInputStyle}
                      checked={savePaymentMethod}
                      onChange={(e) => setSavePaymentMethod(e.target.checked)}
                    />
                    <span>Save this card for next time</span>
                  </label>
                )}

                <AuthorizeNetPaymentForm
                  amountLabel={amountLabel}
                  disabled={disabled}
                  buttonText={isLoading ? "Processing…" : "Enter card details"}
                  onTokenReceived={handleTokenReceived}
                  onError={setPaymentError}
                />
              </>
            )}

            <p
              style={{
                fontSize: 12,
                color: "#888",
                marginTop: 12,
                lineHeight: 1.4,
              }}
            >
              Card details are always encrypted and secure. Fullproof never sees
              or stores your card number.
            </p>
          </div>
        </div>

        <div>
          <div style={styles.summary}>
            <Summary summary={summary} appliedDiscount={appliedDiscount} />
            <div ref={payButtonRef} style={{ height: 30 }} />
            <div style={{ display: "flex", justifyContent: "center" }}>
              <PayButton />
            </div>
          </div>
        </div>
      </div>
      <FAQ />
      <Footer />

      {showStickyFooter && (
        <StickyFooter>
          <div
            style={{
              display: "flex",
              margin: "10px",
              width: "100%",
              justifyContent: "center",
            }}
          >
            <PayButton style={{ maxWidth: 600 }} />
          </div>
        </StickyFooter>
      )}

      <PaymentProcessingOverlay visible={processing} />

      {/*
        Held-for-fraud-review terminal state.

        Authorize.Net responseCode 4 means the card was accepted but the fraud
        filters held the transaction for manual review. It is neither a paid
        order nor a decline, so it must not route to the success page (which
        would imply fulfilment) or show a retry (which would double-charge if
        the hold later clears).
      */}
      {heldForReview && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "white",
              borderRadius: 16,
              padding: "24px 20px 28px",
              width: "100%",
              maxWidth: 480,
              margin: "0 16px",
            }}
          >
            <h3 style={{ margin: "0 0 16px 0", fontSize: 18 }}>
              Your payment is being reviewed
            </h3>
            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: 15,
                lineHeight: 1.5,
                color: "#444",
              }}
            >
              We&rsquo;ve received your payment details and they are going
              through a routine security check. You don&rsquo;t need to do
              anything. We will email you as soon as it clears.
            </p>
            <p style={{ margin: "0 0 24px 0", fontSize: 13, color: "#888" }}>
              Please don&rsquo;t re-submit the payment as that could result in a
              duplicate charge. Questions?{" "}
              <a
                href="mailto:support@getfullproof.com"
                style={{ color: "#007aff" }}
              >
                support@getfullproof.com
              </a>
            </p>
            <button
              style={{
                width: "100%",
                padding: "12px",
                border: "none",
                borderRadius: 50,
                backgroundColor: "#3b72fe",
                color: "white",
                cursor: "pointer",
                fontSize: 16,
                fontWeight: 600,
              }}
              onClick={() => navigate("/", { replace: true })}
            >
              Back to shop
            </button>
          </div>
        </div>
      )}

      {/* Address selection sheet */}
      <Sheet isOpen={isOpen} onClose={() => setOpen(false)}>
        <Sheet.Container>
          <Sheet.Header>
            <div style={{ padding: "15px 20px 0 20px" }}>
              <div style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                Change Address
              </div>
              <div
                style={{
                  marginTop: 15,
                  width: "100%",
                  fontWeight: 400,
                  fontSize: ".9rem",
                }}
              >
                Select the delivery address:
              </div>
            </div>
          </Sheet.Header>
          <div style={{ padding: "0 20px" }}>
            {addresses.map((addr, index) => (
              <div
                key={index}
                style={{ cursor: "pointer" }}
                onClick={() => handleAddressSelect(index)}
              >
                <AddressBox
                  addressObject={addr}
                  selected={tempSelectedAddressIndex === index}
                />
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                style={{
                  backgroundColor: "white",
                  color: "black",
                  padding: "8px 16px",
                  border: "1px solid black",
                  marginTop: "10px",
                  cursor: "pointer",
                  fontSize: "0.9rem",
                  borderRadius: "50px",
                }}
                onClick={handleAddNewAddress}
              >
                <Add
                  style={{
                    fontSize: ".9rem",
                    position: "relative",
                    top: 2,
                    marginRight: 5,
                  }}
                />
                Add new address
              </button>
            </div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button
                style={{
                  backgroundColor: "black",
                  color: "white",
                  padding: "8px 16px",
                  border: "none",
                  width: "100%",
                  margin: "10px auto 30px auto",
                  cursor: "pointer",
                  fontSize: "16px",
                  borderRadius: "50px",
                  maxWidth: "340px",
                }}
                onClick={handleSaveAddressChange}
              >
                Save Changes
              </button>
            </div>
          </div>
        </Sheet.Container>
        <Sheet.Backdrop onTap={() => setOpen(false)} />
      </Sheet>
    </div>
  );
};

const styles = {
  checkoutContainer: {
    width: "100%",
    padding: "0 20px",
    fontFamily: "'Inter', sans-serif",
  },
  orderTitle: { fontSize: "18px", marginBottom: "12px" },
  address: { marginBottom: "20px" },
  linkButton: {
    fontSize: ".8rem",
    color: "black",
    cursor: "pointer",
    border: "1px solid black",
    borderRadius: "50px",
    padding: "5px 10px",
    marginTop: "10px",
    backgroundColor: "white",
  },
  summary: {
    marginTop: "10px",
    padding: "10px 15px 40px",
    background: "rgba(59, 114, 254, 0.05)",
  },
};

export default CheckoutAuthorizeNet;
